import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, FormControlLabel, Switch, MenuItem, Container, Grid, Typography, Alert } from '@mui/material';
import { useFormik } from 'formik';
import { format } from 'date-fns';
import * as Yup from 'yup';

import { FormControlBase, TextFieldBase, SelectBase, FormSectionTitle, InputLabelSelect } from '../../../styles';
import { PedidosContext } from '../../../../../Context/PedidosContext';
import api from '../../../../../Api/apiAxios';

const validationSchema = Yup.object({
  id: Yup.number(),
  tipoSistema: Yup.string().required('O tipo de sistema é obrigatório'),
  numeroSerie: Yup.string(),
  inicioFaturamento: Yup.date().required('A data de início de faturamento é obrigatória').nullable(),
  distribuidor: Yup.number().required('A revenda é obrigatória'),
  cliente: Yup.number().required('O cliente é obrigatório'),
  nomeFantasia: Yup.string().required('O nome fantasia é obrigatório'),
  valorServico: Yup.number().required('O valor do serviço é obrigatório').min(0, 'O valor do serviço não pode ser negativo'),
  quantidadeParcelas: Yup.number().required('A quantidade de parcelas é obrigatória').min(1, 'Deve haver pelo menos uma parcela'),
  valorParcela: Yup.number().min(0, 'O valor da parcela não pode ser negativo'),
  credito: Yup.boolean(),
  integraFaturamentoAnt: Yup.boolean(),
  tipoServico: Yup.number().required('O tipo de serviço é obrigatório').min(1, 'O tipo de serviço é obrigatório'),
  cobrancaDiretaBma: Yup.boolean(),
  quantidadeHoras: Yup.number().min(0, 'A quantidade de horas deve ser maior ou igual a zero'),
  descricaoServico: Yup.string().required('A descrição do serviço é obrigatória').min(10, 'A descrição do serviço deve ter pelo menos 10 caracteres'),
});

export function ModalServico() {
  const idUsuario = window.localStorage.getItem('idUsuario');
  const [sisdmStatus, setSisdmStatus] = React.useState(null);
  const [mensagemErro, setMensagemErro] = React.useState(null);

  const { id } = useParams();
  const { productVariant, dadosTabs, modalServico, setModalServico, fetchTiposServicos, tiposServicos, setServicos, fetchServicos, distribuidores, tipoServicoPadrao } = useContext(PedidosContext);

  const handleSave = async (values) => {
    setMensagemErro(null);

    const regServico = {
      Id: formik.values.id || 0,
      PedidoId: id,
      DistribuidorId: Number(formik.values.distribuidor),
      ClienteId: Number(formik.values.cliente),
      UsuarioId: Number(idUsuario),
      NumeroSerie: formik.values.numeroSerie,
      TipoSistema: Number(formik.values.tipoSistema === 'Ponto' ? 1 : (formik.values.tipoSistema === 'Acesso' ? 2 : 3)),
      TipoServico: Number(formik.values.tipoServico),
      DescricaoServico: formik.values.descricaoServico,
      QuantidadeParcelas: Number(formik.values.quantidadeParcelas),
      StatusServico: 0,
      ValorServico: Number(formik.values.valorServico),
      DataInclusao: new Date(),
      DataLiberacao: new Date(),
      DataPrimeiroVencimento: formik.values.inicioFaturamento,
      Ressarcimento: formik.values.credito ? 1 : 0,
      QuantidadeHoras: formik.values.quantidadeHoras,
      IntegrarMesAnterior: formik.values.integraFaturamentoAnt ? 1 : 0,
      ObservacaoNotaFiscal: formik.values.observacaoNotaFiscal || null,
      CobrancaDiretaBma: formik.values.cobrancaDiretaBma === true,
    };

    try {
      let ret;
      if (modalServico.servico.id) {
        ret = await api.put("Servico/", JSON.stringify(regServico), { headers: { 'Content-Type': 'application/json' } });
      } else {
        ret = await api.post("Servico/", JSON.stringify(regServico), { headers: { 'Content-Type': 'application/json' } });
      }

      setModalServico({ show: false, servico: {} });
      const listaServicos = await fetchServicos(id);
      const servicosOrdenados = listaServicos.sort((a, b) => new Date(b.dataInclusao) - new Date(a.dataInclusao));
      setServicos(servicosOrdenados);
    } catch (error) {
      const msg = error?.response?.data || 'Erro ao salvar serviço.';
      setMensagemErro(msg);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: modalServico.servico.id || 0,
      tipoSistema: productVariant || '',
      numeroSerie: dadosTabs.numeroSerie || '',
      inicioFaturamento: modalServico.servico.dataPrimeiroVencimento
        ? format(new Date(modalServico.servico.dataPrimeiroVencimento), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'),
      distribuidor: dadosTabs.distribuidor || '',
      cliente: dadosTabs.cliente || '',
      nomeFantasia: dadosTabs.nomeFantasia || '',
      valorServico: modalServico.servico.valorServico || '',
      quantidadeParcelas: modalServico.servico.quantidadeParcelas || '',
      valorParcela: modalServico.servico.valorParcela || '',
      credito: modalServico.servico.ressarcimento,
      integraFaturamentoAnt: modalServico.servico.integrarMesAnterior,
      descricaoServico: modalServico.servico.descricaoServico || '',
      tipoServico: modalServico.servico.tipoServico || tipoServicoPadrao || 0,
      quantidadeHoras: modalServico.servico.quantidadeHoras || 0.00,
      observacaoNotaFiscal: modalServico.servico.observacaoNotaFiscal || dadosTabs.observacaoNotaFiscal || '',
      cobrancaDiretaBma: modalServico.servico.id
        ? (modalServico.servico.cobrancaDiretaBma === true)
        : (distribuidores.find(d => d.id === dadosTabs.distribuidor)?.tipoDistribuidor === 1),
    },
    validationSchema,
    onSubmit: (values) => {
      handleSave(values);
    },
  });

  async function validarSisdm(tipoId) {
    setSisdmStatus(null);
    if (!tipoId) return;
    const tipo = tiposServicos.find(t => String(t.id) === String(tipoId));
    if (!tipo || !tipo.codigoServicoSisdm) { setSisdmStatus('sem-codigo'); return; }
    setSisdmStatus('loading');
    try {
      const res = await api.get('/Sisdm/validar-servico/' + tipo.codigoServicoSisdm);
      setSisdmStatus(res.data.valido ? 'valido' : 'invalido');
    } catch { setSisdmStatus(null); }
  }

  useEffect(() => {
    fetchTiposServicos();
    fetchServicos();
  }, [modalServico.show]);

  useEffect(() => {
    if (tiposServicos.length > 0 && formik.values.tipoServico) {
      validarSisdm(formik.values.tipoServico);
    }
  }, [tiposServicos]);

  return (
    <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <Box sx={{
        backgroundColor: 'white',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        width: '55%',
        position: 'fixed',
        top: '6vh',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        maxHeight: '88vh',
        boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.18)',
        overflow: 'hidden',
      }}>
        <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
          <FormSectionTitle label={modalServico.disabled ? 'Visualizar Serviço' : (modalServico.servico.id > 0 ? 'Editar Serviço' : 'Adicionar Serviço')} />
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1, px: 3, pb: 1 }}>
          {mensagemErro && (
            <Alert severity="error" onClose={() => setMensagemErro(null)} sx={{ mt: 1 }}>
              {mensagemErro}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>

            <Grid item xs={12} md={6}>
              <FormControlBase>
                <TextFieldBase
                  type="date"
                  name="inicioFaturamento"
                  label="Início do Faturamento"
                  fullWidth
                  value={formik.values.inicioFaturamento}
                  onChange={formik.handleChange}
                  error={formik.touched.inicioFaturamento && Boolean(formik.errors.inicioFaturamento)}
                  helperText={formik.touched.inicioFaturamento && formik.errors.inicioFaturamento}
                  disabled={modalServico.disabled}
                />
              </FormControlBase>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                {sisdmStatus && (
                  <Typography variant="caption" sx={{
                    position: 'absolute', top: -10, right: 8, zIndex: 1,
                    backgroundColor: 'white', px: 0.5, fontSize: '0.7rem',
                    color: { valido: 'success.main', invalido: 'warning.main', 'sem-codigo': 'error.main', loading: 'text.secondary' }[sisdmStatus]
                  }}>
                    {{ loading: 'Validando...', 'sem-codigo': 'Cód. SISDM não configurado', invalido: '✗ Não encontrado no SISDM', valido: '✓ Código validado no SISDM' }[sisdmStatus]}
                  </Typography>
                )}
                <FormControlBase>
                  <InputLabelSelect id="tipoServico">Tipo de Serviço</InputLabelSelect>
                  <SelectBase
                    id="tipoServico"
                    name="tipoServico"
                    value={formik.values.tipoServico}
                    onChange={(e) => { formik.handleChange(e); validarSisdm(e.target.value); }}
                    error={formik.touched.tipoServico && Boolean(formik.errors.tipoServico)}
                    helperText={formik.touched.tipoServico && formik.errors.tipoServico}
                    disabled={modalServico.disabled}
                    label="Tipo de Serviço"
                    width="100%"
                  >
                    {tiposServicos.map((tipo) => (
                      <MenuItem key={tipo.id} value={tipo.id}>{tipo.tituloTipo}</MenuItem>
                    ))}
                  </SelectBase>
                </FormControlBase>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlBase>
                <TextFieldBase
                  label="Valor do Serviço (R$)"
                  name="valorServico"
                  fullWidth
                  type="number"
                  value={formik.values.valorServico}
                  onChange={formik.handleChange}
                  error={formik.touched.valorServico && Boolean(formik.errors.valorServico)}
                  helperText={formik.touched.valorServico && formik.errors.valorServico}
                  disabled={modalServico.disabled}
                />
              </FormControlBase>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlBase>
                <InputLabelSelect id="quantidadeParcelas">Quantidade de Parcelas</InputLabelSelect>
                <SelectBase
                  id="quantidadeParcelas"
                  label="Quantidade de Parcelas"
                  name="quantidadeParcelas"
                  fullWidth
                  value={formik.values.quantidadeParcelas}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.quantidadeParcelas && Boolean(formik.errors.quantidadeParcelas)}
                  helperText={formik.touched.quantidadeParcelas && formik.errors.quantidadeParcelas}
                  disabled={modalServico.disabled}
                >
                  {[...Array(10)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                  ))}
                </SelectBase>
              </FormControlBase>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlBase>
                <TextFieldBase
                  label="Valor da Parcela (R$)"
                  fullWidth
                  type="number"
                  value={(formik.values.valorServico / formik.values.quantidadeParcelas).toFixed(2) || 0}
                  disabled
                />
              </FormControlBase>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlBase>
                <TextFieldBase
                  label="Quantidade de Horas"
                  fullWidth
                  value={formik.values.quantidadeHoras}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (/^\d*\.?\d*$/.test(value)) formik.setFieldValue("quantidadeHoras", value);
                  }}
                  onBlur={(event) => {
                    const value = event.target.value;
                    formik.setFieldValue("quantidadeHoras", value ? parseFloat(value).toFixed(2) : "0.00");
                  }}
                  error={formik.touched.quantidadeHoras && Boolean(formik.errors.quantidadeHoras)}
                  helperText={formik.touched.quantidadeHoras && formik.errors.quantidadeHoras}
                  name="quantidadeHoras"
                  id="quantidadeHoras"
                  inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                  disabled={modalServico.disabled}
                />
              </FormControlBase>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlBase>
                <TextFieldBase
                  label="Descrição do Serviço"
                  name="descricaoServico"
                  fullWidth
                  multiline
                  rows={4}
                  value={formik.values.descricaoServico}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.descricaoServico && Boolean(formik.errors.descricaoServico)}
                  helperText={formik.touched.descricaoServico && formik.errors.descricaoServico}
                  disabled={modalServico.disabled}
                />
              </FormControlBase>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlBase>
                <TextFieldBase
                  label="Observação de NF"
                  name="observacaoNotaFiscal"
                  fullWidth
                  multiline
                  rows={4}
                  value={formik.values.observacaoNotaFiscal}
                  onChange={formik.handleChange}
                  inputProps={{ maxLength: 1000 }}
                  helperText={`${(formik.values.observacaoNotaFiscal || '').length}/1000`}
                  disabled={modalServico.disabled}
                />
              </FormControlBase>
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.credito === 1}
                    onChange={() => formik.setFieldValue('credito', formik.values.credito === 1 ? 0 : 1)}
                  />
                }
                disabled={modalServico.disabled}
                name="credito"
                label="Crédito"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.integraFaturamentoAnt === 1}
                    onChange={() => formik.setFieldValue('integraFaturamentoAnt', formik.values.integraFaturamentoAnt === 1 ? 0 : 1)}
                  />
                }
                name="integraFaturamentoAnt"
                label="Integrar no faturamento anterior"
                disabled={modalServico.disabled}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.cobrancaDiretaBma === true}
                    onChange={() => formik.setFieldValue('cobrancaDiretaBma', !formik.values.cobrancaDiretaBma)}
                  />
                }
                name="cobrancaDiretaBma"
                label="Cobrança direta pela BMA"
                disabled={modalServico.disabled}
              />
            </Grid>

          </Grid>
        </Box>

        <Box sx={{ px: 3, py: 2, flexShrink: 0, borderTop: '1px solid #f0f0f0' }}>
          {modalServico.disabled ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setModalServico({ show: false, servico: {} })}>
                Voltar
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={() => setModalServico({ show: false, servico: {} })}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={() => formik.handleSubmit()}>
                Salvar
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
